import { ReactElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { EmptyAction } from 'typesafe-actions';
import { PayloadAction } from 'typesafe-actions';

import { ApplicationProps, ApplicationState } from '../ducks/application';
import { AuthState } from '../ducks/auth';
import { SignupState } from '../ducks/signup';
import { MobileVerificationState, updateVerificationCode } from '../ducks/verificationCode';
import { submitVerificationCodeAction } from '../ducks/verificationCode';
import { AuthAction } from '../interfaces/nelo-api/AuthAction';
import { getUtmParam } from '../util/utm-params';

interface StateProps extends ApplicationProps {
  verificationCode: string;
  authAction: AuthAction | null;
  errorMessage: string | null;
}

interface DispatchProps {
  submitVerificationCode: () => void;
  updateVerificationCode: (code: string) => void;
}

const AutoVerificationEntry = (props: StateProps & DispatchProps): ReactElement => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const code: string | undefined = getUtmParam('code')?.toLowerCase();

  useEffect(() => {
    if (code?.length === 5) {
      props.updateVerificationCode(code);
    }
  }, [code]);

  useEffect(() => {
    if (props.verificationCode?.length === 5) {
      props.submitVerificationCode();
    }
  }, [props.verificationCode]);

  useEffect(() => {
    if (props.authAction === 'LOGIN') {
      navigate('/signup/welcome-back');
    }
    if (props.authAction === 'SIGNUP') {
      setTimeout(() => {
        navigate('/signup/curp-generator');
      }, 2000);
    }
  }, [props.authAction]);

  useEffect(() => {
    if (props.errorMessage) {
      setErrorMessage(props.errorMessage);
    }
  }, [props.errorMessage]);

  return (
    <div className="bg-primary h-screen w-screen flex justify-center items-center">
      <div className="flex flex-col items-center gap-6">
        <div className="flex gap-3">
          <div className="w-6 h-6 bg-white rounded-full animate-slow-pulse [animation-delay:0ms]" />
          <div className="w-6 h-6 bg-white rounded-full animate-slow-pulse [animation-delay:500ms]" />
          <div className="w-6 h-6 bg-white rounded-full animate-slow-pulse [animation-delay:1000ms]" />
          <div className="w-6 h-6 bg-white rounded-full animate-slow-pulse [animation-delay:1500ms]" />
        </div>
        {errorMessage && (
          <div className="bg-neloRed/95 p-5 my-5 rounded-xl">
            <div className="text-white text-lg">{t('errors.unexpected')}</div>
            <div
              className="text-white cursor-pointer hover:opacity-70 transition-opacity mt-4 text-center border border-white rounded-3xl p-2 w-[50%] mx-auto"
              onClick={(): void => {
                navigate('/signup/mobile-handoff');
              }}
              role="button"
              tabIndex={0}
            >
              {t('errors.tryAgain')}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const mapDispatchToProps = {
  updateVerificationCode: (code: string): PayloadAction<string, string> => updateVerificationCode(code),
  submitVerificationCode: (): EmptyAction<'mobileVerification/SUBMIT_VERIFICATION_CODE'> =>
    submitVerificationCodeAction()
};

const mapStateToProps = ({
  verificationCode,
  application,
  auth
}: {
  verificationCode: MobileVerificationState;
  application: ApplicationState;
  auth: AuthState;
  signup: SignupState;
}): StateProps => ({
  verificationCode: verificationCode.verificationCode,
  isLoading: application.isLoading && !application.errorMessage,
  authAction: auth.authAction,
  errorMessage: application.errorMessage
});

export default connect(mapStateToProps, mapDispatchToProps)(AutoVerificationEntry);
